import { OpenAiIcon } from '../components/Icons/platformIcons/OpenAi';
import { ZendeskIcon } from '../components/Icons/platformIcons/Zendesk';
import { GenesysIcon } from '../components/Icons/platformIcons/Genesys';
import { Dynamics365Icon } from '../components/Icons/platformIcons/Dynamics365';
import { OpenIdIcon } from '../components/Icons/platformIcons/OpenId';
import { LiveChatIcon } from '../components/Icons/LiveChatIcon';
import { AzureIcon } from '../components/Icons/platformIcons/Azure';
import { HelviaLogoIcon } from '../components/Icons/HelviaLogoIcon';
import { CiscoIcon } from '../components/Icons/CiscoIcon';
import { GeminiIcon } from '../components/Icons/GeminiIcon';
import { IS_AUTOMATED_AGENT_TESTING_ENABLED } from './env';
import i18n from '../i18n';

export const SIDEBAR_GROUPS = {
  DESIGNER: i18n.t('Designer'),
  OBSERVATORY: i18n.t('Observatory'),
  SUPPORT: i18n.t('Customer Support')
};

export const INTEGRATION_TYPE = {
  MICROSOFT_DYNAMICS_KB: 'MICROSOFT_DYNAMICS_KB',
  MICROSOFT_DYNAMICS_CRM: 'MICROSOFT_DYNAMICS_CRM',
  HELVIA_LIVECHAT: 'HELVIA_LIVECHAT',
  CISCO_LIVECHAT: 'CISCO_LIVECHAT',
  ZENDESK_LIVECHAT: 'ZENDESK_LIVECHAT',
  GENESYS_LIVECHAT: 'GENESYS_LIVECHAT',
  OIDC_AUTHENTICATION: 'OIDC_AUTHENTICATION',
  ZENDESK_TICKETING: 'ZENDESK_TICKETING',
  OPEN_AI_LLM: 'OPEN_AI_LLM',
  AZURE_AI_LLM: 'AZURE_AI_LLM',
  OPEN_AI_LLM_TOPIC_MODELLING: 'OPEN_AI_LLM',
  OPEN_AI_LLM_SUMMARIZATION: 'OPEN_AI_LLM',
  AZURE_AI_LLM_SUMMARIZATION: 'AZURE_AI_LLM',
  OPEN_AI_LLM_SEMANTIC_SEARCH_NODE: 'OPEN_AI_LLM',
  AZURE_AI_LLM_SEMANTIC_SEARCH_NODE: 'AZURE_AI_LLM',
  GEMINI_AI_LLM: 'GEMINI_AI_LLM',
  GEMINI_AI_LLM_SUMMARIZATION: 'GEMINI_AI_LLM',
  GEMINI_AI_LLM_SEMANTIC_SEARCH_NODE: 'GEMINI_AI_LLM',
  OPEN_AI_LLM_AUTOMATED_AGENT_TESTING: 'OPEN_AI_LLM',
  OPEN_AI_LLM_LANGUAGE_DETECTION: 'OPEN_AI_LLM',
  AZURE_AI_LLM_LANGUAGE_DETECTION: 'AZURE_AI_LLM',
  GEMINI_AI_LLM_LANGUAGE_DETECTION: 'GEMINI_AI_LLM'
};

export const INTEGRATION_CATEGORY = {
  KB: 'KB',
  LIVECHAT: 'LIVECHAT',
  CRM: 'CRM',
  LLM: 'LLM',
  AUTHENTICATION: 'AUTHENTICATION',
  TICKETING: 'TICKETING'
};

export const PLUGIN_CATEGORY = {
  KB: 'KB',
  LIVECHAT: 'LIVECHAT',
  CRM: 'CRM',
  AUTHENTICATION: 'AUTHENTICATION',
  TICKETING: 'TICKETING',
  LLM_NODE: 'LLM_NODE',
  LLM_TOPIC_MODELLING: 'LLM_TOPIC_MODELLING',
  LLM_SUMMARIZATION: 'LLM_SUMMARIZATION',
  LLM_SEMANTIC_SEARCH_NODE: 'LLM_SEMANTIC_SEARCH_NODE',
  LLM_AUTOMATED_AGENT_TESTING: 'LLM_AUTOMATED_AGENT_TESTING',
  LLM_LANGUAGE_DETECTION: 'LLM_LANGUAGE_DETECTION'
};

export const LLM_PROVIDERS_ALIASES = {
  GEMINI: {
    key: 'gemini',
    type: INTEGRATION_TYPE.GEMINI_AI_LLM
  },
  AZURE_AI: {
    key: 'azureai',
    type: INTEGRATION_TYPE.AZURE_AI_LLM
  },
  OPEN_AI: {
    key: 'openai',
    type: INTEGRATION_TYPE.OPEN_AI_LLM
  }
};

/**
 * A list of integrations that can be used with the platform. Also referred as Integration Tools.
 * type: The type of the integration.
 * category: The category of the integration.
 * renderTypeIcon: A function that returns the type icon of the integration.
 * renderCategoryIcon: A function that returns the category icon of the integration.
 * hidden: Hides the integration from places where the user can create it (org integrations table). Connecting is still allowed.
 * pluginCategory: The plugin category that the integration belongs to.
 * hasPluginSettings: Whether or not this integration requires seperate plugin configuration
 */
export const INTEGRATIONS = [
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_NODE,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.AZURE_AI_LLM,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <AzureIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_NODE,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.GEMINI_AI_LLM,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <GeminiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_NODE,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM_TOPIC_MODELLING,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_TOPIC_MODELLING,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM_AUTOMATED_AGENT_TESTING,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: !IS_AUTOMATED_AGENT_TESTING_ENABLED,
    pluginCategory: PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM_SUMMARIZATION,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_SUMMARIZATION,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.AZURE_AI_LLM_SUMMARIZATION,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <AzureIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_SUMMARIZATION,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.GEMINI_AI_LLM_SUMMARIZATION,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <GeminiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_SUMMARIZATION,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM_SEMANTIC_SEARCH_NODE,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.AZURE_AI_LLM_SEMANTIC_SEARCH_NODE,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <AzureIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.GEMINI_AI_LLM_SEMANTIC_SEARCH_NODE,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <GeminiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: true,
    pluginCategory: PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.MICROSOFT_DYNAMICS_KB,
    category: INTEGRATION_CATEGORY.KB,
    renderTypeIcon: (props) => <Dynamics365Icon {...props} />,
    renderCategoryIcon: (props) => <Dynamics365Icon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.KB,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.HELVIA_LIVECHAT,
    category: INTEGRATION_CATEGORY.LIVECHAT,
    renderTypeIcon: (props) => <HelviaLogoIcon {...props} />,
    renderCategoryIcon: (props) => <LiveChatIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LIVECHAT,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.CISCO_LIVECHAT,
    category: INTEGRATION_CATEGORY.LIVECHAT,
    renderTypeIcon: (props) => <CiscoIcon {...props} />,
    renderCategoryIcon: (props) => <LiveChatIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LIVECHAT,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.ZENDESK_LIVECHAT,
    category: INTEGRATION_CATEGORY.LIVECHAT,
    renderTypeIcon: (props) => <ZendeskIcon {...props} />,
    renderCategoryIcon: (props) => <LiveChatIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LIVECHAT,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.GENESYS_LIVECHAT,
    category: INTEGRATION_CATEGORY.LIVECHAT,
    renderTypeIcon: (props) => <GenesysIcon {...props} />,
    renderCategoryIcon: (props) => <LiveChatIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LIVECHAT,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM,
    category: INTEGRATION_CATEGORY.CRM,
    renderTypeIcon: (props) => <Dynamics365Icon {...props} />,
    renderCategoryIcon: (props) => <Dynamics365Icon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.CRM,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.OIDC_AUTHENTICATION,
    category: INTEGRATION_CATEGORY.AUTHENTICATION,
    renderTypeIcon: (props) => <OpenIdIcon {...props} />,
    renderCategoryIcon: (props) => <OpenIdIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.AUTHENTICATION,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.ZENDESK_TICKETING,
    category: INTEGRATION_CATEGORY.TICKETING,
    renderTypeIcon: (props) => <ZendeskIcon {...props} />,
    renderCategoryIcon: (props) => <ZendeskIcon {...props} />,
    hidden: false,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.TICKETING,
    hasPluginSettings: false
  },
  {
    type: INTEGRATION_TYPE.OPEN_AI_LLM_LANGUAGE_DETECTION,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <OpenAiIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION,
    hasPluginSettings: true
  },
  {
    type: INTEGRATION_TYPE.AZURE_AI_LLM_LANGUAGE_DETECTION,
    category: INTEGRATION_CATEGORY.LLM,
    renderTypeIcon: (props) => <AzureIcon {...props} />,
    renderCategoryIcon: (props) => <OpenAiIcon {...props} />,
    hidden: true,
    isPluginHidden: false,
    pluginCategory: PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION,
    hasPluginSettings: true
  }
];

export const PLUGINS = [
  {
    category: PLUGIN_CATEGORY.LLM_NODE,
    sidebarGroup: SIDEBAR_GROUPS.DESIGNER,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: false,
    allowMultipleIntegrations: true
  },
  {
    category: PLUGIN_CATEGORY.KB,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.KB,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LIVECHAT,
    sidebarGroup: SIDEBAR_GROUPS.SUPPORT,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LIVECHAT,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.CRM,
    sidebarGroup: SIDEBAR_GROUPS.SUPPORT,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.CRM,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.AUTHENTICATION,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.AUTHENTICATION,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.TICKETING,
    sidebarGroup: SIDEBAR_GROUPS.SUPPORT,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.TICKETING,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LLM_SEMANTIC_SEARCH_NODE,
    sidebarGroup: SIDEBAR_GROUPS.DESIGNER,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LLM_SUMMARIZATION,
    sidebarGroup: SIDEBAR_GROUPS.OBSERVATORY,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION,
    sidebarGroup: SIDEBAR_GROUPS.DESIGNER,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LLM_TOPIC_MODELLING,
    sidebarGroup: SIDEBAR_GROUPS.OBSERVATORY,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: false,
    allowMultipleIntegrations: false
  },
  {
    category: PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING,
    sidebarGroup: SIDEBAR_GROUPS.OBSERVATORY,
    supportedIntegrationCategory: INTEGRATION_CATEGORY.LLM,
    hidden: !IS_AUTOMATED_AGENT_TESTING_ENABLED,
    allowMultipleIntegrations: false
  }
];
